import React, { useState, useEffect, useRef, useLayoutEffect } from 'react'
import { Outlet } from 'react-router-dom'
import propTypes from 'prop-types'
import Card from '../Card/Card'
import projects from '../../../assets/projects'
import TextLink from '../TextLink/TextLink'
import { useBp } from '../../../utils/Breakpoints/Breakpoints'
import FaIcon from '../FaIcon/FaIcon'

import './CardGroup.scss'

const CardGroup = ({ featured, moreProjects, currentProject }) => {
  const featuredProjectList = projects.filter((project) => project.featured)

  const publishedProjects = projects.filter(
    (item) => item.status === 'published'
  )

  const primaryProjects = publishedProjects.filter(
    (project) => !project.overflow
  )
  const overflowProjects = publishedProjects.filter(
    (project) => project.overflow
  )
  const [showOverflow, setShowOverflow] = useState(() => {
    const saved = localStorage.getItem('portfolioShowOverflow')
    return saved === 'true'
  })

  useEffect(() => {
    localStorage.setItem('portfolioShowOverflow', showOverflow)
  }, [showOverflow])

  const allButCurrentProjects = primaryProjects
    .filter((item) => item.url !== currentProject)
    .sort(() => 0.5 - Math.random())
    .slice(0, 2)

  const [filter, setFilter] = useState(primaryProjects)

  const getUniqueTags = (array) => [...new Set(array.map((item) => item.tag))]
  const getTag = (tag) => {
    return primaryProjects.filter((item) => item.tag === tag)
  }

  // Sort projects by year from newest to oldest
  // const projectsByNewest = featured
  //   ? featuredProjects.sort((a, b) => b.year - a.year)
  //   : filter.sort((a, b) => b.year - a.year)

  const featuredClass = featured ? 'featured' : ''
  const moreProjectsClass = moreProjects ? 'more-projects' : ''
  const belowSm = useBp('< sm')
  const cardType = belowSm ? 'narrow' : 'wide'

  const myRef = useRef()

  const [headerHeight, setHeaderHeight] = useState(null)
  useLayoutEffect(() => {
    myRef.current &&
      setTimeout(() => {
        setHeaderHeight(myRef.current.getBoundingClientRect().height)
      }, 0)
  }, [myRef])

  return (
    <div className={`card-group ${featuredClass} ${moreProjectsClass}`}>
      {featured ? ( // Featured projects
        <header className='card-group-header' style={{ height: headerHeight }}>
          <h2 className='small-caps' ref={myRef}>
            Featured Work
          </h2>
          <TextLink
            to='/work'
            label={`View all (${primaryProjects.length})`}
            iconRight='chevron-right'
          />
        </header>
      ) : // More projects
      moreProjects ? (
        <div className='card-group-header'>
          <h2 className='small-caps'>More Work</h2>
        </div>
      ) : (
        // Filtered projects
        <div className='filters'>
          <button
            className={`filter-tag ${
              getUniqueTags(filter).every(
                (value, i) => value === getUniqueTags(primaryProjects)[i]
              ) &&
              getUniqueTags(filter).length ===
                getUniqueTags(primaryProjects).length
                ? 'active'
                : ''
            }`}
            onClick={() => setFilter(primaryProjects)}>
            {`All (${primaryProjects.length})`}
          </button>
          {getUniqueTags(primaryProjects).map((tag) => {
            return (
              <button
                className={`filter-tag ${
                  getUniqueTags(filter).includes(tag) &&
                  getUniqueTags(filter).length === 1
                    ? 'active'
                    : ''
                }`}
                key={tag}
                onClick={() => setFilter(getTag(tag))}>
                <span>{`${tag} (${getTag(tag).length})`}</span>
              </button>
            )
          })}
        </div>
      )}

      <div className='card-group-container'>
        {featured // Featured projects
          ? featuredProjectList.map((project) => (
              <Card key={project.url} type={cardType} project={project} />
            ))
          : moreProjects // More projects
            ? allButCurrentProjects.map((project) => (
                <Card key={project.url} type='narrow' project={project} />
              ))
            : filter.map(
                // Filtered projects
                (project) => (
                  <Card key={project.url} type={cardType} project={project} />
                )
              )}
        <Outlet />
      </div>

      {overflowProjects.length > 0 && !featured && !moreProjects && (
        <div className='overflow-section'>
          <div
            className='overflow-toggle'
            onClick={() => setShowOverflow(!showOverflow)}>
            <div className='text-container'>
              <span>Other Work</span>
              <FaIcon
                name={showOverflow ? 'chevron-up' : 'chevron-down'}
                size={12}
              />
            </div>
            <div className='line' />
          </div>
          {showOverflow && (
            <div className='card-group-container overflow-grid'>
              {overflowProjects.map((project) => (
                <Card key={project.url} type='narrow' project={project} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

CardGroup.defaultProps = {
  featured: false,
  moreProjects: false,
  currentProject: null,
}

CardGroup.propTypes = {
  featured: propTypes.bool,
  moreProjects: propTypes.bool,
  currentProject: propTypes.string,
}

export default CardGroup
